import { useState } from "react";
import { faAdd, faCodeBranch, faCheck, faSpinner, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import crudApi from "@serie3/common/api/crudApi";
import PipelineConfig from "@serie3/freelancesplaceapi/Config/Crm/PipelineConfig";
import NewModal from "../../../layout/Popups/NewModal";
import CompleteDropdown from "../../../layout/Dropdowns/CompleteDropdown";
import AiResearchButton from "./Shared/AiResearchButton";

const ContactCard = ({
  index,
  contact,
  setShowDetails,
  createNew,
  setViewData,
  deleteData,
  className,
}) => {
  const url      = useLocation();
  const navigate = useNavigate();

  const [showCrmModal, setShowCrmModal]       = useState(false);
  const [showAiResearch, setShowAiResearch]   = useState(false);
  const [projects, setProjects]               = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [addingId, setAddingId]               = useState(null);
  const [addedId, setAddedId]                 = useState(null);

  const openCrmModal = async () => {
    setShowCrmModal(true);
    setLoadingProjects(true);
    setAddedId(null);
    const res = await crudApi.justGet(PipelineConfig.project.getAll);
    const data = res?.data;
    setProjects(data?.projects || data?.items || data || []);
    setLoadingProjects(false);
  };

  const addToProject = async (projectId) => {
    if (addingId) return;
    setAddingId(projectId);
    const isCompany = !!contact.firmenname;
    const body = isCompany ? { company_id: contact.id } : { person_id: contact.id };
    await crudApi.createNew(
      `${PipelineConfig.project.addContact}${projectId}/addcontact`,
      body,
      null,
      true
    );
    setAddingId(null);
    setAddedId(projectId);
  };

  const Name = () => (
    <div style={{
      minHeight: "72px",
      padding: "16px 48px 16px 20px",
      background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
      color: "white",
      borderRadius: "10px 10px 0 0",
      position: "relative",
    }}>
      <h5 style={{ fontWeight: 700, marginBottom: "4px", letterSpacing: "0.01em" }}>
        {contact.firmenname
          ? contact.firmenname
          : `${contact.vorname || ""} ${contact.name || ""}`.trim() || "—"}
      </h5>
      {contact.position && (
        <span style={{ fontSize: "0.78rem", fontWeight: 400, opacity: 0.85 }}>
          {contact.position}
        </span>
      )}

      <div style={{ position: "absolute", top: "10px", right: "10px" }}>
        <CompleteDropdown
          viewAction={setShowDetails ? () => { setViewData(contact); setShowDetails(true); } : undefined}
          editAction={url?.pathname ? () => navigate(url.pathname + "/edit/" + contact.id) : undefined}
          deleteAction={deleteData ? () => deleteData(index, contact) : undefined}
          extraItems={contact?.id ? [
            {
              label: "AI Research",
              icon: faMagnifyingGlass,
              iconBg: "#ede9fe",
              iconColor: "#7c3aed",
              onClick: () => setShowAiResearch(true),
            },
            {
              label: "Add to Pipeline",
              icon: faCodeBranch,
              iconBg: "#e8f5e9",
              iconColor: "#2e7d32",
              onClick: openCrmModal,
            },
          ] : []}
        />
      </div>
    </div>
  );

  const Address = () => {
    if (!contact.adresse?.length) return null;
    const a = contact.adresse[0];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <span style={{ fontSize: "0.82rem", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Address</span>
        <span style={{ fontWeight: 500, color: "#212529" }}>{a.strasse} {a.hausnummer}</span>
        <span style={{ fontSize: "0.85rem", color: "#495057" }}>{a.plz} {a.ort}</span>
      </div>
    );
  };

  const Contact = () => {
    if (!contact.kontaktdaten?.length) return null;
    const k = contact.kontaktdaten[0];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <span style={{ fontSize: "0.82rem", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{k.key_name}</span>
        <span style={{ fontWeight: 500, color: "#212529" }}>{k.value}</span>
      </div>
    );
  };

  const CrmModal = () => (
    <NewModal
      show={showCrmModal}
      setShow={setShowCrmModal}
      title="Add to CRM Project"
    >
      {loadingProjects ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.5rem" }} />
        </div>
      ) : projects.length === 0 ? (
        <p style={{ color: "#888", textAlign: "center", padding: "24px 0" }}>No CRM projects found.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {projects.map(project => {
            const isAdded   = addedId  === project.id;
            const isLoading = addingId === project.id;
            return (
              <button
                key={project.id}
                onClick={() => addToProject(project.id)}
                disabled={!!addingId || isAdded}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  background: isAdded ? "#f0fff4" : "#fff",
                  cursor: isAdded || addingId ? "default" : "pointer",
                  fontWeight: 500,
                  fontSize: "0.92rem",
                  color: isAdded ? "#2e7d32" : "#212529",
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={e => { if (!isAdded && !addingId) e.currentTarget.style.borderColor = "#1a73e8"; }}
                onMouseLeave={e => { if (!isAdded && !addingId) e.currentTarget.style.borderColor = "#e0e0e0"; }}
              >
                <span>{project.title || project.name || `Project ${project.id}`}</span>
                {isLoading && <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#1a73e8" }} />}
                {isAdded   && <FontAwesomeIcon icon={faCheck}   style={{ color: "#2e7d32" }} />}
              </button>
            );
          })}
        </div>
      )}
    </NewModal>
  );

  if (typeof contact === "undefined") {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          onClick={createNew}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: "12px", flex: 1, cursor: "pointer",
            background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
            border: "2px dashed #93b4f5", borderRadius: "10px", color: "#1a73e8",
          }}
        >
          <div style={{
            width: "48px", height: "48px", borderRadius: "50%", background: "#1a73e8",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.1rem", color: "#fff", boxShadow: "0 4px 12px rgba(26,115,232,0.35)",
          }}>
            <FontAwesomeIcon icon={faAdd} />
          </div>
          <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#1a73e8" }}>Add New</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CrmModal />
      {showAiResearch && (
        <AiResearchButton
          personId={!contact.firmenname ? contact.id : undefined}
          companyId={contact.firmenname ? contact.id : undefined}
          autoTrigger
          onClose={() => setShowAiResearch(false)}
        />
      )}
      <div style={{ flex: 1, border: "none", borderRadius: "10px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", background: "#fff", display: "flex", flexDirection: "column" }}>
        <Name />
        <div className="card-body container-fluid" style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <Row>
            <Col><Address /></Col>
            <Col><Contact /></Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ContactCard;
